import { useState, type FormEvent } from 'react';
import { createRole } from '@/services/users';

interface RoleModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export default function RoleModal({ onClose, onCreated }: RoleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('El nombre del rol es obligatorio.');
      return;
    }
    setSaving(true);
    try {
      await createRole(name, description);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el rol.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 border border-background-200">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground-950">Nuevo rol</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Crea un rol y luego configura sus permisos.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Nombre del rol</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Vendedor, Promotor, Bodega"
              className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Acceso limitado a pedidos e inventario"
              rows={3}
              maxLength={500}
              className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 resize-none"
            />
          </div>

          <div className="pt-2 flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-4 py-2.5 rounded-lg border border-background-200 text-foreground-700 hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
            >
              {saving ? 'Creando...' : 'Crear rol'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}